// Types

export interface DriverRevenueData {
  driver: string;
  fullName: string;
  driverId: string | null;
  revenuePerMile: number;
  costPerMile: number;
  profitPerMile: number;
  totalMiles: number;
}

export interface ProfitabilityTrendData {
  month: string;
  year: string;
  [driverName: string]: number | string;
}

export interface DriverMetrics {
  driver: string;
  driverId: string | null;
  status: 'Active' | 'Inactive';
  profitMargin: number;
  profit: number;
  milesThisMonth: number;
  revenueThisMonth: number;
}

// Cost breakdown data - supports dynamic categories (static + custom)
export interface CostBreakdownData {
  driver: string;
  driverId: string | null;
  [category: string]: number | string | null; // Dynamic category costs
}

// Category metadata for display purposes
export interface CategoryMetadata {
  category: string;
  displayName: string;
  emojiUnicode: string;
  isCustom: boolean;
}

// Full response from cost breakdown API
export interface CostBreakdownResponse {
  drivers: CostBreakdownData[];
  categoryMetadata: Record<string, CategoryMetadata>;
}

export interface SummaryMetrics {
  totalGrossProfit: number;
  avgProfitMargin: number;
  totalRevenue: number;
  totalExpenses: number;
  totalMiles: number;
  avgRevenuePerDay: number;
  avgMilesPerDay: number;
  driverCount: number;
  // Business-bucket totals (money not assigned to any truck); not in totalRevenue/totalExpenses
  businessRevenue: number;
  businessExpenses: number;
}

export interface DriverInfo {
  driverId: string;
  name: string;
}

export interface DriverPerformanceProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

// Constants

// Static expense categories with their default colors
export const STATIC_EXPENSE_CATEGORY_COLORS: Record<string, string> = {
  fuel: '#005005',
  preventative_maintenance: '#6A9E68',
  repairs: '#a0d911',
  tires: '#13c2c2',
  insurance: '#722ed1',
  truck_payments: '#007bff',
  licensing: '#eb2f96',
  tolls: '#faad14',
};

// Colors for custom categories (cycle through these)
export const CUSTOM_CATEGORY_COLORS = [
  '#ff7875',
  '#ffc069',
  '#95de64',
  '#5cdbd3',
  '#69c0ff',
  '#b37feb',
  '#ff85c0',
];

// Legacy constant for backwards compatibility with demo data
export const EXPENSE_CATEGORIES = [
  { key: 'fuel', label: 'Fuel', color: '#005005' },
  {
    key: 'preventative_maintenance',
    label: 'Preventative Maintenance',
    color: '#6A9E68',
  },
  { key: 'repairs', label: 'Repairs', color: '#a0d911' },
  { key: 'tires', label: 'Tires', color: '#13c2c2' },
  { key: 'insurance', label: 'Insurance', color: '#722ed1' },
  { key: 'truck_payments', label: 'Truck Payments', color: '#007bff' },
  { key: 'licensing', label: 'Licensing', color: '#eb2f96' },
  { key: 'tolls', label: 'Tolls', color: '#faad14' },
] as const;

/**
 * Build expense categories for chart rendering from API categoryMetadata
 * Returns array of { key, label, color } for each category
 */
export const buildExpenseCategoriesFromMetadata = (
  categoryMetadata: Record<string, CategoryMetadata>
): Array<{ key: string; label: string; color: string }> => {
  let customColorIndex = 0;

  return Object.entries(categoryMetadata).map(([key, meta]) => {
    let color: string;

    if (meta.isCustom) {
      // Assign color from custom category colors, cycling through
      color =
        CUSTOM_CATEGORY_COLORS[
          customColorIndex % CUSTOM_CATEGORY_COLORS.length
        ];
      customColorIndex++;
    } else {
      // Use static category color
      color = STATIC_EXPENSE_CATEGORY_COLORS[key] || '#8884d8';
    }

    return {
      key,
      label: meta.displayName,
      color,
    };
  });
};

export const CHART_COLORS = {
  success: '#64f51d',
  warning: '#faad14',
  danger: '#f5222d',
  purple: '#722ed1',
  cyan: '#13c2c2',
  lime: '#a0d911',
  blue: '#007bff',
  primary: '#1D213B',
  magenta: '#eb2f96',
  secondary: '#6A9E68',
  dark_green: '#005005',
  dark_blue: '#005005',
  dark_red: '#8B0000', // Dark red for negative profit margins
};

// Colors for line charts - typed array for proper TypeScript support
export const LINE_COLORS: string[] = [
  '#6A9E68', // secondary
  '#64f51d', // success
  '#faad14', // warning
  '#722ed1', // purple
  '#13c2c2', // cyan
  '#a0d911', // lime
  '#007bff', // blue
  '#eb2f96', // magenta
  '#f5222d', // danger
];

// Demo Data for Pre-Active Customers

export const getDemoSummaryMetrics = (): SummaryMetrics => ({
  totalGrossProfit: 35800,
  avgProfitMargin: 22.9,
  totalRevenue: 156400,
  totalExpenses: 120600,
  totalMiles: 61000,
  avgRevenuePerDay: 5213,
  avgMilesPerDay: 2033,
  driverCount: 5,
  businessRevenue: 0,
  businessExpenses: 4200,
});

export const getDemoDriverMetrics = (): DriverMetrics[] => [
  {
    driver: 'Average Driver',
    driverId: null,
    status: 'Active',
    profitMargin: 22.9,
    profit: 7160,
    milesThisMonth: 12200,
    revenueThisMonth: 31280,
  },
  {
    driver: 'John Smith',
    driverId: '1',
    status: 'Active',
    profitMargin: 25.3,
    profit: 8500,
    milesThisMonth: 12000,
    revenueThisMonth: 33600,
  },
  {
    driver: 'Mike Johnson',
    driverId: '2',
    status: 'Active',
    profitMargin: 18.7,
    profit: 5200,
    milesThisMonth: 11500,
    revenueThisMonth: 27800,
  },
  {
    driver: 'Robert Williams',
    driverId: '3',
    status: 'Active',
    profitMargin: 28.1,
    profit: 9800,
    milesThisMonth: 15000,
    revenueThisMonth: 34900,
  },
  {
    driver: 'David Brown',
    driverId: '4',
    status: 'Inactive',
    profitMargin: 15.2,
    profit: 4100,
    milesThisMonth: 9000,
    revenueThisMonth: 27000,
  },
  {
    driver: 'James Davis',
    driverId: '5',
    status: 'Active',
    profitMargin: 24.8,
    profit: 8200,
    milesThisMonth: 13500,
    revenueThisMonth: 33100,
  },
];

export const getDemoRevenueVsCostData = (): DriverRevenueData[] => [
  {
    driver: 'Average',
    fullName: 'Average Driver',
    driverId: null,
    revenuePerMile: 2.56,
    costPerMile: 1.98,
    profitPerMile: 0.59,
    totalMiles: 12200,
  },
  {
    driver: 'John',
    fullName: 'John Smith',
    driverId: '1',
    revenuePerMile: 2.8,
    costPerMile: 2.09,
    profitPerMile: 0.71,
    totalMiles: 12000,
  },
  {
    driver: 'Mike',
    fullName: 'Mike Johnson',
    driverId: '2',
    revenuePerMile: 2.42,
    costPerMile: 1.97,
    profitPerMile: 0.45,
    totalMiles: 11500,
  },
  {
    driver: 'Robert',
    fullName: 'Robert Williams',
    driverId: '3',
    revenuePerMile: 2.33,
    costPerMile: 1.67,
    profitPerMile: 0.65,
    totalMiles: 15000,
  },
  {
    driver: 'David',
    fullName: 'David Brown',
    driverId: '4',
    revenuePerMile: 3.0,
    costPerMile: 2.54,
    profitPerMile: 0.46,
    totalMiles: 9000,
  },
  {
    driver: 'James',
    fullName: 'James Davis',
    driverId: '5',
    revenuePerMile: 2.45,
    costPerMile: 1.84,
    profitPerMile: 0.61,
    totalMiles: 13500,
  },
];

export const getDemoProfitabilityTrend = (): ProfitabilityTrendData[] => [
  {
    month: 'Jan',
    year: '2024',
    'Average Driver': 20.5,
    'John Smith': 22.1,
    'Mike Johnson': 16.8,
    'Robert Williams': 25.2,
    'David Brown': 14.5,
    'James Davis': 23.9,
  },
  {
    month: 'Feb',
    year: '2024',
    'Average Driver': 21.2,
    'John Smith': 23.5,
    'Mike Johnson': 17.2,
    'Robert Williams': 26.1,
    'David Brown': 13.8,
    'James Davis': 25.4,
  },
  {
    month: 'Mar',
    year: '2024',
    'Average Driver': 22.8,
    'John Smith': 25.0,
    'Mike Johnson': 18.5,
    'Robert Williams': 27.8,
    'David Brown': 15.2,
    'James Davis': 27.5,
  },
  {
    month: 'Apr',
    year: '2024',
    'Average Driver': 21.5,
    'John Smith': 24.2,
    'Mike Johnson': 17.8,
    'Robert Williams': 26.5,
    'David Brown': 14.0,
    'James Davis': 25.0,
  },
  {
    month: 'May',
    year: '2024',
    'Average Driver': 23.1,
    'John Smith': 26.0,
    'Mike Johnson': 19.2,
    'Robert Williams': 28.5,
    'David Brown': 15.8,
    'James Davis': 26.0,
  },
  {
    month: 'Jun',
    year: '2024',
    'Average Driver': 22.5,
    'John Smith': 25.3,
    'Mike Johnson': 18.7,
    'Robert Williams': 28.1,
    'David Brown': 15.2,
    'James Davis': 24.8,
  },
];

export const getDemoCostBreakdown = (): CostBreakdownData[] => [
  {
    driver: 'Average',
    driverId: null,
    fuel: 3200,
    preventative_maintenance: 450,
    repairs: 320,
    tires: 180,
    insurance: 520,
    truck_payments: 1200,
    licensing: 85,
    tolls: 245,
  },
  {
    driver: 'John',
    driverId: '1',
    fuel: 3400,
    preventative_maintenance: 380,
    repairs: 250,
    tires: 150,
    insurance: 520,
    truck_payments: 1200,
    licensing: 85,
    tolls: 280,
  },
  {
    driver: 'Mike',
    driverId: '2',
    fuel: 3100,
    preventative_maintenance: 520,
    repairs: 420,
    tires: 200,
    insurance: 520,
    truck_payments: 1200,
    licensing: 85,
    tolls: 220,
  },
  {
    driver: 'Robert',
    driverId: '3',
    fuel: 2900,
    preventative_maintenance: 350,
    repairs: 180,
    tires: 120,
    insurance: 520,
    truck_payments: 1200,
    licensing: 85,
    tolls: 195,
  },
  {
    driver: 'David',
    driverId: '4',
    fuel: 3600,
    preventative_maintenance: 680,
    repairs: 550,
    tires: 280,
    insurance: 520,
    truck_payments: 1200,
    licensing: 85,
    tolls: 310,
  },
  {
    driver: 'James',
    driverId: '5',
    fuel: 3000,
    preventative_maintenance: 420,
    repairs: 200,
    tires: 150,
    insurance: 520,
    truck_payments: 1200,
    licensing: 85,
    tolls: 220,
  },
];
